<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useAudioPlayer } from '@/composables/useAudioPlayer';
import { useSdk } from '@/composables/useSdk';
import StarRating from '../StarRating.vue';

interface Props {
  postUlid?: string;
  wsomEntryUlid?: string;
  listenTimeSeconds?: number;
  ratingThreshold?: number;
  compact?: boolean;
  disabled?: boolean;
  readonly?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  postUlid: undefined,
  wsomEntryUlid: undefined,
  listenTimeSeconds: 0,
  ratingThreshold: 15,
  compact: false,
  disabled: false,
  readonly: false,
});

const emit = defineEmits<{
  (e: 'rate', rating: number): void;
  (e: 'remove'): void;
}>();

const { sdk } = useSdk();
const audioPlayer = useAudioPlayer();

// Rating state
const userRating = ref<number | null>(null);
const isRatingLoading = ref(false);
const ratingJustUnlocked = ref(false);

// Get listen time from audioPlayer if not provided
const effectiveListenTime = computed(() => {
  if (props.listenTimeSeconds > 0) return props.listenTimeSeconds;
  return Math.floor(audioPlayer.currentTime.value);
});

// Rating unlock logic
const canRate = computed(() => {
  return effectiveListenTime.value >= props.ratingThreshold || userRating.value !== null;
});

const secondsUntilCanRate = computed(() => {
  if (userRating.value !== null) return 0;
  const remaining = props.ratingThreshold - effectiveListenTime.value;
  return Math.max(0, Math.ceil(remaining));
});

// Watch for rating unlock animation
watch(canRate, (newVal, oldVal) => {
  if (newVal && !oldVal) {
    ratingJustUnlocked.value = true;
    setTimeout(() => {
      ratingJustUnlocked.value = false;
    }, 1000);
  }
});

// Load user rating from track data or cache
function loadUserRating() {
  const track = audioPlayer.currentTrack.value;
  if (!track) {
    userRating.value = null;
    return;
  }

  const trackUlid = props.postUlid || track.ulid || track.id;

  // Check the shared rating cache first
  if (trackUlid) {
    const cache = audioPlayer.ratingCache;
    if (cache?.value) {
      const cachedRating = cache.value.get(trackUlid) || 0;
      if (cachedRating > 0) {
        userRating.value = cachedRating;
        return;
      }
    }
  }

  // Fall back to the track data
  const rating = (track as Record<string, unknown>).userRating as number | undefined;
  userRating.value = rating ?? null;
}

// Watch for track changes
watch(
  () => audioPlayer.currentTrack.value?.ulid,
  () => {
    loadUserRating();
  },
  { immediate: true }
);

// Handle rating
async function handleRate(rating: number) {
  const track = audioPlayer.currentTrack.value;
  if (!track || !sdk) return;

  const trackUlid = props.postUlid || track.ulid || track.id;
  if (!trackUlid) return;

  isRatingLoading.value = true;
  try {
    if (props.wsomEntryUlid) {
      // Use WSOM rating endpoint
      const { wsomService } = await import('@/services/wsomService');
      await wsomService.rateEntry(props.wsomEntryUlid, rating);
    } else {
      // Use standard post rating endpoint
      await sdk.ratePost(trackUlid, rating);
    }

    userRating.value = rating;

    // Update shared rating cache
    const cache = audioPlayer.ratingCache;
    if (cache?.value) {
      if (rating === 0) {
        cache.value.delete(trackUlid);
      } else {
        cache.value.set(trackUlid, rating);
      }
      cache.value = new Map(cache.value);
    }

    emit('rate', rating);

    // Skip to next if zero rating
    if (rating === 0 && audioPlayer.queueIndex.value < audioPlayer.queue.value.length - 1) {
      audioPlayer.skipNext();
    }
  } catch (error) {
    console.error('Failed to rate song:', error);
    loadUserRating();
  } finally {
    isRatingLoading.value = false;
  }
}

// Handle removing rating
async function handleRemoveRating() {
  const track = audioPlayer.currentTrack.value;
  if (!track || !sdk) return;

  const trackUlid = props.postUlid || track.ulid || track.id;
  if (!trackUlid) return;

  isRatingLoading.value = true;
  try {
    await sdk.removeRating(trackUlid);
    userRating.value = null;

    // Update shared rating cache
    const cache = audioPlayer.ratingCache;
    if (cache?.value) {
      cache.value.delete(trackUlid);
      cache.value = new Map(cache.value);
    }

    emit('remove');
  } catch (error) {
    console.error('Failed to remove rating:', error);
    loadUserRating();
  } finally {
    isRatingLoading.value = false;
  }
}
</script>

<template>
  <div class="rating-section" :class="{ 'rating-section--compact': compact }">
    <!-- Countdown Timer (shown while locked) -->
    <div
      v-if="!canRate"
      class="rating-countdown"
      :class="{ 'rating-unlocked-animation': ratingJustUnlocked }"
    >
      <v-icon
        icon="mdi-timer-sand"
        :size="compact ? 16 : 20"
        color="white"
        class="countdown-icon"
      />
      <span class="countdown-text">{{ secondsUntilCanRate }}s{{ compact ? '' : ' to rate' }}</span>
    </div>

    <!-- Star Rating (shown after countdown) -->
    <StarRating
      v-else
      v-model="userRating"
      :disabled="disabled || isRatingLoading"
      class="rating-section__stars"
      :class="{ 'rating-unlocked-animation': ratingJustUnlocked }"
      @rate="handleRate"
      @remove="handleRemoveRating"
    />
  </div>
</template>

<style scoped lang="scss">
.rating-section {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;

  &--compact {
    min-height: 32px;
  }

  &__stars {
    // Inherits StarRating styles
  }
}

.rating-countdown {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  backdrop-filter: blur(10px);

  .countdown-icon {
    animation: pulse 2s ease-in-out infinite;
  }

  .countdown-text {
    font-size: 0.875rem;
    font-weight: 600;
    color: white;
    min-width: 28px;
    text-align: center;
  }
}

.rating-section--compact .rating-countdown {
  padding: 4px 8px;

  .countdown-text {
    font-size: 0.75rem;
  }
}

.rating-unlocked-animation {
  animation: unlockPulse 0.6s ease-out;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

@keyframes unlockPulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}
</style>
